/**
 * Product Parameters Configuration Component
 */
import React, { useState } from 'react';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { Parameter, ParameterType } from '../../types/parameters';

const ProductParameters: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedProduct, setSelectedProduct] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingParam, setEditingParam] = useState<Parameter | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    param_name: 'seasonality_profile',
    profile_type: 'year_round',
    peak_multiplier: 1.5,
    lifecycle_stage: 'mature',
    shelf_life_days: 365,
  });

  // Fetch products
  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await api.getProducts();
      return response.data;
    },
  });

  // Fetch product parameters
  const { data: parameters } = useQuery<Parameter[]>({
    queryKey: ['parameters', 'product', selectedProduct],
    queryFn: async () => {
      const response = await api.getParameters({
        param_type: ParameterType.PRODUCT,
        entity_id: selectedProduct,
      });
      return response.data;
    },
    enabled: !!selectedProduct,
  });

  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      if (editingParam) {
        return await api.updateParameter(editingParam.param_id, data);
      } else {
        return await api.createParameter(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parameters'] });
      setDialogOpen(false);
      setEditingParam(null);
      setSuccessMessage('Parameter saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (paramId: string) => api.deleteParameter(paramId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parameters'] });
    },
  });

  const handleSave = () => {
    const paramValue: Record<string, any> = {};

    switch (formData.param_name) {
      case 'seasonality_profile':
        paramValue.profile_type = formData.profile_type;
        paramValue.peak_multiplier = formData.peak_multiplier;
        break;
      case 'lifecycle_stage':
        paramValue.stage = formData.lifecycle_stage;
        break;
      case 'shelf_life_days':
        paramValue.days = formData.shelf_life_days;
        break;
    }

    saveMutation.mutate({
      param_type: ParameterType.PRODUCT,
      entity_id: selectedProduct,
      param_name: formData.param_name,
      param_value: paramValue,
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {successMessage && <Alert severity="success" sx={{ mb: 3 }}>{successMessage}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Select Product</InputLabel>
            <Select
              value={selectedProduct}
              label="Select Product"
              onChange={(e) => setSelectedProduct(e.target.value)}
            >
              {products?.map((product: any) => (
                <MenuItem key={product.product_id} value={product.product_id}>
                  {product.product_name} ({product.sku})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {selectedProduct && (
          <Grid item xs={12} md={6}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              Add Parameter
            </Button>
          </Grid>
        )}
      </Grid>

      {selectedProduct && (
        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Parameter</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parameters?.map((param) => (
                <TableRow key={param.param_id}>
                  <TableCell><Chip label={param.param_name} size="small" /></TableCell>
                  <TableCell>
                    <Typography variant="caption" component="pre">
                      {JSON.stringify(param.param_value, null, 2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => { setEditingParam(param); setDialogOpen(true); }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => deleteMutation.mutate(param.param_id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )) || (
                <TableRow>
                  <TableCell colSpan={3} align="center">No parameters configured</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add/Edit Product Parameter</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Parameter Type</InputLabel>
                <Select
                  value={formData.param_name}
                  label="Parameter Type"
                  onChange={(e) => setFormData({ ...formData, param_name: e.target.value })}
                >
                  <MenuItem value="seasonality_profile">Seasonality Profile</MenuItem>
                  <MenuItem value="lifecycle_stage">Lifecycle Stage</MenuItem>
                  <MenuItem value="shelf_life_days">Shelf Life</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.param_name === 'seasonality_profile' && (
              <>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Profile Type</InputLabel>
                    <Select
                      value={formData.profile_type}
                      label="Profile Type"
                      onChange={(e) => setFormData({ ...formData, profile_type: e.target.value })}
                    >
                      <MenuItem value="summer">Summer</MenuItem>
                      <MenuItem value="winter">Winter</MenuItem>
                      <MenuItem value="year_round">Year Round</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Peak Multiplier"
                    value={formData.peak_multiplier}
                    onChange={(e) => setFormData({ ...formData, peak_multiplier: parseFloat(e.target.value) })}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductParameters;
